import { ChangeEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { Modal, TokenLogo } from 'components';
import { TOKEN_BY_ADDRESS, WETH_SUPPORTED_CHAINS } from 'consts';
import { store } from 'store';
import { bn } from 'utils/BigNumber';
import { cutTokenName, cutTokenSymbol, getBalance, toDollars } from 'utils/helpers';
import { Address, isAddress } from 'viem';

import { TokenSearch } from '../TokenSearch/TokenSearch';

import * as S from './TokenSelectModal.styles';
import { TokenSelectSettings } from './TokenSelectSettings';

export const TokenSelectModal = observer(() => {
  const { chainId, balances, wrappedNativeToken } = store.web3Store;
  const { tokens, prices, fixedTokens, customTokens, addFixedToken, removeFixedToken } =
    store.commonStore;
  const { isTokenSelectOpen, tokenSelectField, closeTokenSelect, selectToken } = store.swapStore;

  const [value, setValue] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  let excludeTokens: Address[] | undefined;

  if (tokenSelectField === 'out') {
    excludeTokens = WETH_SUPPORTED_CHAINS.includes(chainId)
      ? undefined
      : [wrappedNativeToken.address];
  }

  useEffect(() => {
    if (!isTokenSelectOpen) {
      setTimeout(() => setValue(''), 600);
    }
  }, [isTokenSelectOpen]);

  useEffect(() => {
    if (customTokens.length === 0) {
      setIsSettingsOpen(false);
    }
  }, [customTokens]);

  const viewTokens = useMemo(() => {
    return tokens
      .filter((t) => !excludeTokens?.includes(t.address))
      .filter((t) => (t.address + t.symbol + t.name).toLowerCase().includes(value.toLowerCase()))
      .map((t) => {
        const balance = getBalance(balances, t.address);
        const balanceHuman = balance && bn(balance).fromDecimals(t.decimals).toString();
        const price = prices[t.address];
        const balanceDollars = price && balanceHuman && bn(balanceHuman).mul(price).toString();
        const fixedIndex = fixedTokens.indexOf(t.address);
        const isFixed = fixedIndex !== -1;

        return {
          ...t,
          balance,
          balanceHuman,
          price,
          balanceDollars,
          fixedIndex,
          isFixed,
        };
      })
      .sort((a, b) => {
        let aBalance = a.balanceDollars && a.balanceDollars !== '0' && a.balanceDollars;
        let bBalance = b.balanceDollars && b.balanceDollars !== '0' && b.balanceDollars;
        if (aBalance && bBalance) return bn(aBalance as string).gt(bBalance as string) ? -1 : 1;
        if (aBalance) return -1;
        if (bBalance) return 1;

        aBalance = a.balance && a.balance !== '0' && a.balance;
        bBalance = b.balance && b.balance !== '0' && b.balance;
        if (aBalance && bBalance) return bn(aBalance as string).gt(bBalance as string) ? -1 : 1;
        if (aBalance) return -1;
        if (bBalance) return 1;

        if (a.isFixed && !b.isFixed) return -1;
        if (b.isFixed && !a.isFixed) return 1;
        if (a.isFixed && b.isFixed) return a.fixedIndex - b.fixedIndex;
        if (a.tags?.includes('native')) return -1;
        if (b.tags?.includes('native')) return 1;
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenSelectField, balances, fixedTokens, prices, tokens, value]);

  const handleFixButtonClick = (
    ev: MouseEvent<HTMLButtonElement>,
    tokenAddress: Address,
    isActive: boolean
  ) => {
    ev.stopPropagation();
    if (isActive) {
      removeFixedToken(tokenAddress);
    } else {
      addFixedToken(tokenAddress);
    }
  };

  const shouldSearchToken =
    viewTokens.length === 0 &&
    isAddress(value) &&
    !excludeTokens?.includes(value) &&
    value.toLowerCase() !== '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'.toLowerCase();

  const handleClose = () => {
    closeTokenSelect();
    setTimeout(() => {
      setIsSettingsOpen(false);
    }, 500);
  };

  const handleSelect = (address: Address) => {
    selectToken(tokenSelectField, address);
    handleClose();
  };

  const handleClear = () => {
    setValue('');
    inputRef.current?.focus();
  };

  return (
    <Modal isOpen={isTokenSelectOpen} onClose={handleClose} maxWidth={460} title="Select Token">
      <S.Header>
        <S.Title>{isSettingsOpen ? 'Custom Tokens' : 'Select token'}</S.Title>
        <S.HeaderButtons>
          {customTokens.length > 0 && (
            <S.SettingsButton onClick={() => setIsSettingsOpen(!isSettingsOpen)}>
              {isSettingsOpen ? '←' : '⚙'}
            </S.SettingsButton>
          )}
        </S.HeaderButtons>
      </S.Header>

      <S.ModalContent $hidden={isSettingsOpen}>
        <S.SearchWrapper>
          <S.SearchInput
            ref={inputRef}
            value={value}
            onChange={(ev: ChangeEvent<HTMLInputElement>) => setValue(ev.target.value)}
            placeholder="Search name or paste address..."
            autoFocus
          />
          {value !== '' && <S.SearchClearButton onClick={handleClear}>✕</S.SearchClearButton>}
        </S.SearchWrapper>

        {shouldSearchToken && (
          <>
            <S.DividerLine />
            <TokenSearch address={value as Address} onImport={handleSelect} />
          </>
        )}

        {fixedTokens.length !== 0 && !shouldSearchToken && (
          <S.FixedTokens>
            {fixedTokens
              .filter((address) => !excludeTokens?.includes(address))
              .map((address) => {
                const token = TOKEN_BY_ADDRESS[chainId]?.[address];
                if (!token) return null;
                return (
                  <S.FixedToken key={address} onClick={() => handleSelect(address)}>
                    <TokenLogo token={token} size={20} />
                    <span>{cutTokenSymbol(token.symbol, 20)}</span>
                    <S.FixedTokenRemove
                      onClick={(ev: MouseEvent<HTMLButtonElement>) => {
                        ev.stopPropagation();
                        handleFixButtonClick(ev, address, true);
                      }}
                    >
                      ✕
                    </S.FixedTokenRemove>
                  </S.FixedToken>
                );
              })}
          </S.FixedTokens>
        )}

        {!shouldSearchToken && <S.DividerLine />}

        {viewTokens.length > 0 && !shouldSearchToken ? (
          <S.TokenList>
            {viewTokens.map((token) => {
              const balanceView = token.balance
                ? bn(token.balance).toView(token.decimals)
                : undefined;

              return (
                <S.TokenItem key={token.address} onClick={() => handleSelect(token.address)}>
                  <TokenLogo token={token} size={32} />
                  <S.TokenInfo>
                    <S.TokenSymbol>{cutTokenName(token.name)}</S.TokenSymbol>
                    <S.TokenName>
                      {balanceView && `${balanceView} `}
                      {cutTokenSymbol(token.symbol, 20)}
                    </S.TokenName>
                  </S.TokenInfo>
                  <S.TokenBalanceCol>
                    {token.balanceDollars && token.balanceDollars !== '0' && (
                      <S.TokenDollars>
                        {toDollars(token.balanceDollars, { withTilde: false })}
                      </S.TokenDollars>
                    )}
                  </S.TokenBalanceCol>
                  <S.PinButton
                    $isActive={token.isFixed}
                    onClick={(ev: MouseEvent<HTMLButtonElement>) =>
                      handleFixButtonClick(ev, token.address, token.isFixed)
                    }
                  >
                    📌
                  </S.PinButton>
                </S.TokenItem>
              );
            })}
          </S.TokenList>
        ) : (
          !shouldSearchToken && <S.EmptyMessage>No tokens found</S.EmptyMessage>
        )}
      </S.ModalContent>

      {isSettingsOpen && (
        <S.SettingsContent>
          <TokenSelectSettings />
        </S.SettingsContent>
      )}
    </Modal>
  );
});
